import React, { useEffect, useMemo, useRef, useState } from "react";
import { Product, ProductVariant } from "~/services/product";
import { transformProductItem } from "~/utils/transform";
import CartIcon from "~/assets/svgs/bag.svg";
import Button from "~/components/Button";
import SelectedProductItem from "~/components/Items/SelectedProductItem";
import classNames from "classnames";
import ChangeVariantModal from "~/components/Modals/ChangeVariantModal";
import { useRecoilValue } from "recoil";
import { CartAtom } from "~/services/cart";
import { useCart } from "~/data/cart";
import { trackingAddToCartClick } from "~/services/tracking";
import Container from "~/components/Container";
import { useLazyQuery } from "@apollo/client";
import { QUERY_GET_LIST_BUNDLE_PRODUCTS } from "~/data/home";

interface BundleContentProps {
  products?: any[];
  skus?: string[];
}

interface VariantInfo {
  attributes: any[];
  product: Partial<Product>;
}

export interface VariantSelection {
  sku: string;
  quantity: number;
  cartId: string;
  parentSku: string;
  productNote: { name: string; type: string; value: string }[];
}

const BundleContent: React.FC<BundleContentProps> = ({
  products: initialProducts = [],
  skus = [],
}) => {
  const { addMultipleToCart, loadingAddMultipleToCart } = useCart();
  const cart = useRecoilValue(CartAtom.cartSelector);
  const [products, setProducts] = useState<any[]>([]);
  const hasFetchedRef = useRef(false);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set(Array.isArray(skus) ? skus : [])
  );
  const [variantState, setVariantState] = useState<Record<string, VariantInfo>>(
    {}
  );
  const [selectedVariant, setSelectedVariant] = useState<
    Record<string, VariantSelection>
  >({});

  const [modalState, setModalState] = useState<{
    open: boolean;
    product: Partial<Product> | null;
  }>({
    open: false,
    product: null,
  });
  const [validationErrors, setValidationErrors] = useState<Set<string>>(
    new Set()
  );

  const [getProducts, { loading: loadingProducts }] = useLazyQuery(
    QUERY_GET_LIST_BUNDLE_PRODUCTS,
    {
      context: { fetchOptions: { method: "GET" } },
      onCompleted: (data) => {
        const fetchedProducts = data?.products?.items || [];
        setProducts(fetchedProducts);
      },
    }
  );

  // Initialize products from props or fetch from skus
  useEffect(() => {
    if (initialProducts.length > 0) {
      // If products are provided directly, use them
      setProducts(initialProducts);
      hasFetchedRef.current = true;
    } else if (skus.length > 0 && !hasFetchedRef.current) {
      // If only skus are provided, fetch the products
      hasFetchedRef.current = true;
      getProducts({
        variables: {
          skus: skus,
        },
      });
    }
  }, [skus, initialProducts.length, getProducts]);

  const transformedProducts = useMemo(() => {
    return products?.map((product) => transformProductItem(product));
  }, [products]);

  const handleProductSelect = (product: Partial<Product>) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(product.sku || "")) {
        newSet.delete(product.sku || "");
      } else {
        newSet.add(product.sku || "");
      }
      return newSet;
    });

    // Clear validation error when product selection changes
    setValidationErrors((prev) => {
      const newErrors = new Set(prev);
      newErrors.delete(product.sku || "");
      return newErrors;
    });
  };

  const handleChangeVariant = (product: Partial<Product>) => {
    // If no variant has been selected yet, initialize with first variant
    if (!variantState[product.sku || ""] && product.variants?.length) {
      const firstVariant = product.variants[0];
      setVariantState((prev) => ({
        ...prev,
        [product.sku || ""]: {
          attributes: firstVariant.attributes,
          product: product,
        },
      }));
    }

    setModalState({
      open: true,
      product,
    });
  };

  const onChangeSelectedVariantQuantity = (sku: string, quantity: number) => {
    setSelectedVariant((prev) => ({
      ...(prev || {}),
      [sku]: {
        ...(prev[sku] || {}),
        quantity: quantity,
      },
    }));
  };

  const handleModalConfirm = (
    data: {
      selectedVariant: Partial<ProductVariant>;
      productNote: { name: string; type: string; value: string }[];
    },
    parentSKU: string
  ) => {
    setSelectedVariant((prev) => ({
      ...(prev || {}),
      [parentSKU]: {
        sku: data.selectedVariant?.product?.sku || "",
        quantity: prev[parentSKU]?.quantity || 1,
        cartId: cart?.cartId || "",
        parentSku: parentSKU,
        ...(data.productNote && { productNote: data.productNote }),
      },
    }));
    setModalState({ open: false, product: null });

    // Clear validation error when variant is selected
    setValidationErrors((prev) => {
      const newErrors = new Set(prev);
      newErrors.delete(parentSKU);
      return newErrors;
    });
  };

  const handleModalCancel = () => {
    setModalState({ open: false, product: null });
  };

  const totalPrice = useMemo(() => {
    return transformedProducts?.reduce((sum, product) => {
      if (selectedProducts.has(product.sku || "")) {
        const variantInfo = variantState[product.sku || ""];
        const variantSelection = selectedVariant[product.sku || ""];
        const quantity = variantSelection?.quantity || 1;
        const price = variantInfo
          ? variantInfo.product?.price_range?.minimum_price?.final_price
              ?.value || 0
          : product.price_range?.minimum_price?.final_price?.value || 0;
        return sum + price * quantity;
      }
      return sum;
    }, 0);
  }, [transformedProducts, selectedProducts, variantState, selectedVariant]);

  useEffect(() => {
    if (modalState.open && modalState.product) {
      setVariantState((prev) => {
        if (
          !prev[modalState.product!.sku || ""] &&
          modalState.product!.variants?.length
        ) {
          const firstVariant = modalState.product!.variants[0];
          return {
            ...prev,
            [modalState.product!.sku || ""]: {
              attributes: firstVariant.attributes,
              product: modalState.product!,
            },
          };
        }
        return prev;
      });
    }
  }, [modalState]);

  const handleAddToCart = async () => {
    if (!cart.cartId) return;

    // Get only selected products
    const filteredProducts = transformedProducts.filter((product) =>
      selectedProducts.has(product.sku || "")
    );

    // Validate that all selected products have variants chosen
    const errors = new Set<string>();
    filteredProducts.forEach((product) => {
      const variantSelection = selectedVariant[product.sku || ""];
      if (!variantSelection || !variantSelection.sku) {
        errors.add(product.sku || "");
      }
    });

    // If there are validation errors, set them and don't proceed
    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }

    // Clear any previous validation errors
    setValidationErrors(new Set());

    // Build cart items array for single mutation call
    const cartItems = filteredProducts
      .map((product) => {
        const variantSelection = selectedVariant[product.sku || ""];

        if (variantSelection && variantSelection.sku) {
          // Find the actual variant object
          const variant = product.variants?.find(
            (v: any) => v.product.sku === variantSelection.sku
          );

          if (variant) {
            // Track add to cart for each item
            trackingAddToCartClick({
              variant: variant,
              product: product,
              qty: variantSelection.quantity || 1,
              cartId: cart.cartId,
            });

            return {
              parent_sku: product.sku || "",
              data: {
                quantity: variantSelection.quantity || 1,
                sku: variantSelection.sku,
              },
              ...(variantSelection.productNote &&
                variantSelection.productNote.length > 0 && {
                  product_note: variantSelection.productNote,
                }),
            };
          }
        }
        return null;
      })
      .filter((item) => item !== null);

    // Add all products to cart in a single mutation call
    await addMultipleToCart({
      variables: {
        cartId: cart.cartId as any,
        cartItems,
        openCart: true,
      },
    });
  };

  if (loadingProducts) {
    return (
      <Container className="container mx-auto px-4 py-8">
        <div className="text-center">Loading products...</div>
      </Container>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Container className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-7">
        {transformedProducts.map((product, index) => {
          const hasError = validationErrors.has(product.sku || "");
          return (
            <div key={product.sku || index} className="flex flex-col gap-2">
              <div
                className={classNames(
                  "relative p-4 border-2 border-solid rounded-lg overflow-hidden",
                  {
                    "border-red-500": hasError,
                    "border-[#333333]": !hasError,
                  }
                )}
              >
                <div
                  className={classNames(
                    "cursor-pointer absolute top-0 left-0 bottom-0 right-0 z-10 bg-[#88888880] transition-opacity duration-300 ease-in-out",
                    {
                      "opacity-0 pointer-events-none": selectedProducts.has(
                        product.sku || ""
                      ),
                      "opacity-100": !selectedProducts.has(product.sku || ""),
                    }
                  )}
                  onClick={() => handleProductSelect(product)}
                ></div>
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(product.sku || "")}
                    onChange={() => handleProductSelect(product)}
                    className="w-5 h-5 cursor-pointer accent-primary"
                  />
                </div>
                <SelectedProductItem
                  data={product}
                  className="border !rounded-lg p-2"
                  onProductClick={() => handleProductSelect(product)}
                  onChangeVariant={handleChangeVariant}
                  selectedVariant={selectedVariant?.[product.sku || ""]}
                  onChangeSelectedVariantQuantity={
                    onChangeSelectedVariantQuantity
                  }
                />
              </div>
              {hasError && (
                <span className="text-red-500 text-sm">
                  * You haven't selected an option for this product yet.
                </span>
              )}
            </div>
          );
        })}
      </div>

      {selectedProducts.size > 0 && (
        <div className="">
          <Button
            onClick={handleAddToCart}
            loading={loadingAddMultipleToCart}
            className="container mx-auto flex items-center justify-center gap-6 font-semibold py-3 px-6 hover:scale-105 !rounded-lg mt-10"
          >
            <CartIcon width={30} height={30} alt="bag" color="white" />
            <div className="flex items-center gap-4">
              <span className="text-lg font-bold text-white">
                ${totalPrice.toFixed(2)}
              </span>
            </div>
            <p className="text-white text-lg font-bold !mb-0 border-l-2 border-white border-solid pl-4 ml-2">
              Add to Cart
            </p>
          </Button>
        </div>
      )}

      <ChangeVariantModal
        open={modalState.open}
        product={modalState.product || undefined}
        defaultVariant={
          selectedVariant?.[modalState.product?.sku || ""]?.sku
            ? variantState[
                modalState.product?.sku || ""
              ]?.product?.variants?.find(
                (v) =>
                  v.product.sku ===
                  selectedVariant?.[modalState.product?.sku || ""]?.sku
              )
            : modalState.product?.sku
              ? variantState[modalState.product.sku]?.product?.variants?.[0]
              : undefined
        }
        defaultProductNote={
          selectedVariant?.[modalState.product?.sku || ""]?.productNote
        }
        loading={false}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
        isNew={!selectedVariant?.[modalState.product?.sku || ""]?.sku}
      />
    </Container>
  );
};

export default BundleContent;
